import React, { forwardRef, useImperativeHandle, useRef } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Stack, TextColors, Typography } from '@sureco/design-system'
import { Button, Divider, Empty } from '@surecompanies/core_components'
import { CompanyStatusEnum } from 'constants/company'
import HrisSelect from 'enrollment-platform/components/form/HrisSelect'
import IndustrySelect from 'enrollment-platform/components/form/IndustrySelect/IndustrySelect'
import StateSelect from 'enrollment-platform/components/form/StateSelect'
import RHFInput from 'enrollment-platform/components/form/hookform/Input/RHFInput'
import RHFMobileInput from 'enrollment-platform/components/form/hookform/MobileInput/RHFMobileInput'
import RHFSelect from 'enrollment-platform/components/form/hookform/Select/RHFSelect'
import useWindowDimensions from 'hooks/useWindowDimensions'
import ContentSection from 'layouts/ContentSection/ContentSection'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { CommissionPartner, CustomerStatus } from '../constants'
import { ContactType } from '../enums/contact.type'
import ContactForm from './ContactForm'
import styles from './CustomerForm.module.scss'
import UploadButton from './UploadButton'
import { CustomerSchema } from './customer.schema'

export type FormRef = {
  submit: () => void
}

type CustomerFormProps = {
  initialValues?: Record<string, any>
  onFormSubmit: (data: any) => void
  isLoading: boolean
  isCreate?: boolean
}

type FormData = z.infer<typeof CustomerSchema>

const CustomerForm = forwardRef<FormRef, CustomerFormProps>(
  ({ initialValues, onFormSubmit, isCreate = false }, ref) => {
    const { handleSubmit, control, setValue } = useForm<FormData>({
      defaultValues: initialValues ?? {},
      resolver: zodResolver(CustomerSchema),
    })
    const form = useWatch({
      control,
    })
    const logoValue = useWatch({
      control,
      name: 'logo',
    })
    const { fields, append, remove } = useFieldArray({
      control,
      name: 'contacts',
      shouldUnregister: true,
    })
    const formRef = useRef<HTMLFormElement>(null)
    const { width } = useWindowDimensions()
    const isSmallScreen = width < 600

    // Determine which logo to show: base64 from form if available, otherwise existing logo URL
    const displayLogo = logoValue || initialValues?.companyLogoUrl
    const statusOptions =
      isCreate || initialValues?.status === CompanyStatusEnum.DRAFT
        ? CustomerStatus
        : CustomerStatus.filter(
            (status) => status.value !== CompanyStatusEnum.DRAFT
          )

    useImperativeHandle(ref, () => ({
      submit: () => {
        formRef?.current?.requestSubmit()
      },
    }))

    const handleImageUploaded = (base64: string) => {
      setValue('logo', base64)
    }

    return (
      <Stack space={20}>
        <ContentSection title="Customer Details">
          <form
            onSubmit={handleSubmit((data) => onFormSubmit(data))}
            ref={formRef}
          >
            <Stack>
              <Stack>
                <UploadButton
                  onImageUploaded={handleImageUploaded}
                  accept="image/jpeg,image/png,image/jpg"
                  logo={displayLogo}
                />
              </Stack>
              <Divider />
              <section className={styles.formLayout}>
                <Stack>
                  <Typography size={14}>
                    Company Name
                    <Typography color={TextColors.error}> *</Typography>
                  </Typography>
                  <RHFInput
                    control={control}
                    name="name"
                    placeholder="Company Name"
                    disabled={!!initialValues}
                    size={isSmallScreen ? 'large' : 'middle'}
                  />
                </Stack>
                <Stack>
                  <Typography size={14}>Phone number</Typography>
                  <RHFMobileInput control={control} name="phoneNumber" />
                </Stack>
                <Stack>
                  <Typography size={14}>Payroll/HRIS Vendor</Typography>
                  <HrisSelect<FormData>
                    showSearch
                    control={control}
                    optionFilterProp="label"
                    name="payrollVendorName"
                    size={isSmallScreen ? 'large' : 'middle'}
                    allowClear
                  />
                </Stack>
                <Stack>
                  <Typography size={14}>
                    Street Address
                    <Typography color={TextColors.error}> *</Typography>
                  </Typography>
                  <RHFInput
                    placeholder="Street Address 1"
                    control={control}
                    name="address.streetAddressLine1"
                    size={isSmallScreen ? 'large' : 'middle'}
                  />
                </Stack>
                <Stack>
                  <Typography size={14}>Street Address 2</Typography>
                  <RHFInput
                    placeholder="Street Address 2"
                    control={control}
                    name="address.streetAddressLine2"
                    size={isSmallScreen ? 'large' : 'middle'}
                  />
                </Stack>

                <Stack>
                  <Typography size={14}>Industry</Typography>
                  <IndustrySelect<FormData>
                    showSearch
                    optionFilterProp="label"
                    control={control}
                    name="industry"
                    allowClear
                  />
                </Stack>
                <Stack space={20}>
                  <Stack>
                    <Typography size={14}>Website URL</Typography>
                    <RHFInput
                      control={control}
                      name="webSiteUrl"
                      placeholder="https://"
                      size={isSmallScreen ? 'large' : 'middle'}
                    />
                  </Stack>
                  <Stack>
                    <Typography size={14} color="#003146">
                      Commission Partner
                    </Typography>
                    <RHFSelect<FormData>
                      control={control}
                      name="commissionPartner"
                      size={isSmallScreen ? 'large' : 'middle'}
                      options={CommissionPartner}
                      showSearch
                      optionFilterProp="label"
                      allowClear
                    />
                  </Stack>
                </Stack>
                <Stack space={20}>
                  <Stack>
                    <Typography size={14}>Business Structure</Typography>
                    <RHFSelect<FormData>
                      showSearch
                      optionFilterProp="label"
                      control={control}
                      name="businessStructure"
                      allowClear
                      options={[
                        { label: 'C Corp', value: 'CCORP' },
                        { label: 'S Corp', value: 'SCORP' },
                        { label: 'LLC', value: 'LLC' },
                        {
                          label: 'Sole Proprietorship',
                          value: 'SOLEPROPRIETORSHIP',
                        },
                        { label: 'Partnership', value: 'PARTNERSHIP' },
                        { label: 'Non-Profit', value: 'NONPROFIT' },
                      ]}
                      size={isSmallScreen ? 'large' : 'middle'}
                    />
                  </Stack>
                </Stack>
                <Stack
                  space={20}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto auto',
                  }}
                >
                  <Stack>
                    <Typography size={14}>City</Typography>
                    <RHFInput
                      control={control}
                      placeholder="Chicago"
                      name="address.city"
                      size={isSmallScreen ? 'large' : 'middle'}
                    />
                  </Stack>
                  <Stack>
                    <Typography size={14}>County</Typography>
                    <RHFInput
                      control={control}
                      placeholder="Cook"
                      name="address.county"
                      size={isSmallScreen ? 'large' : 'middle'}
                    />
                  </Stack>
                  <Stack>
                    <Typography size={14}>State</Typography>
                    <StateSelect<FormData>
                      control={control}
                      showSearch
                      optionFilterProp="label"
                      name="address.stateProvince"
                      placeholder="Select"
                      size={isSmallScreen ? 'large' : 'middle'}
                    />
                  </Stack>
                  <Stack>
                    <Typography size={14}>Zip</Typography>
                    <RHFInput
                      control={control}
                      placeholder="00000"
                      name="address.postalCode"
                      size={isSmallScreen ? 'large' : 'middle'}
                    />
                  </Stack>
                </Stack>
              </section>
            </Stack>
            <Divider />
            <Stack
              space={20}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto auto auto auto',
              }}
            >
              <Stack>
                <Typography size={14} color="#003146">
                  Customer Status{' '}
                  <Typography color={TextColors.error}> *</Typography>
                </Typography>
                <RHFSelect<FormData>
                  control={control}
                  name="status"
                  options={statusOptions}
                  defaultValue={initialValues?.status}
                  optionFilterProp="label"
                  size={isSmallScreen ? 'large' : 'middle'}
                />
              </Stack>
            </Stack>
          </form>
        </ContentSection>
        <ContentSection title="Contact Details">
          {fields.length === 0 ? (
            <Empty
              image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
              description={<Typography>No contacts yet</Typography>}
            >
              <Button
                type="primary"
                onClick={() =>
                  append({
                    type: ContactType.HR,
                    email: '',
                    title: '',
                    phone: {
                      number: '',
                    },
                    fullName: '',
                    isPrimary: false,
                    fundingReportDistribution: false,
                    isVisibleToEmployees: false,
                  })
                }
              >
                Create Now
              </Button>
            </Empty>
          ) : (
            <Stack space={20}>
              {fields.map((field, index) => (
                <div key={field.id}>
                  <ContactForm
                    name={`contacts.${index}`}
                    control={control}
                    onRemove={() => remove(index)}
                    // @ts-ignore
                    contacts={form.contacts}
                  />
                </div>
              ))}
              <div style={{ textAlign: 'center' }}>
                <Button
                  type="primary"
                  onClick={() =>
                    append({
                      type: ContactType.HR,
                      email: '',
                      title: '',
                      phone: {
                        number: '',
                      },
                      fullName: '',
                      isPrimary: false,
                      fundingReportDistribution: false,
                      isVisibleToEmployees: false,
                    })
                  }
                >
                  Add Another Contact
                </Button>
              </div>
            </Stack>
          )}
        </ContentSection>
      </Stack>
    )
  }
)

export default CustomerForm
